import {
    clampSproutStreamSelection,
    DEFAULT_SPROUT_STREAM_MODEL_ID,
    DEFAULT_SPROUT_STREAM_REASONING_EFFORT,
    getSproutStreamSelection,
    parseSproutStreamOverrides,
    resolveMemberAgent,
    SPROUT_AGENT,
    type SproutStreamModelId,
    type SproutStreamReasoningEffort,
} from '@fruitful/llm-service-shared';
import { MastraClientError } from '@mastra/client-js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { isWindowDefined } from 'smoothie';

import { StorageKeys } from '../../common/constants';
import { useStorage } from '../../common/providers/StorageProvider';
import { captureException } from '../../logger/Logger';
import { useMastraClient } from '../providers/MastraClient';
import {
    DEFAULT_SPROUT_STREAM_SELECTION,
    readSproutStreamOptionsStore,
    readSproutStreamSelectionForThread,
    readStoredMemberAgentKey,
    type SproutStreamSelection,
    writeSproutStreamSelection,
    writeStoredMemberAgentKey,
} from '../utils/sproutStreamOptionsStorage';
import { useShowSproutDevTools } from './useShowSproutDevTools';

const isThreadNotFoundError = (err: unknown): boolean =>
    err instanceof MastraClientError
        ? err.status === 404
        : err instanceof Error && /\bstatus:\s*404\b/i.test(err.message);

export const useSproutStreamOptions = (threadId: string | undefined) => {
    const showDevTools = useShowSproutDevTools();
    const storage = useStorage();
    const mastraClient = useMastraClient();
    const mastraClientRef = useRef(mastraClient);
    mastraClientRef.current = mastraClient;
    const threadIdRef = useRef(threadId);
    threadIdRef.current = threadId;
    const selectionRef = useRef<SproutStreamSelection>(
        DEFAULT_SPROUT_STREAM_SELECTION,
    );

    const [modelId, setModelIdState] = useState<SproutStreamModelId>(
        DEFAULT_SPROUT_STREAM_MODEL_ID,
    );
    const [reasoningEffort, setReasoningEffortState] =
        useState<SproutStreamReasoningEffort>(
            DEFAULT_SPROUT_STREAM_REASONING_EFFORT,
        );
    const [agentKey, setAgentKeyState] = useState(() =>
        readStoredMemberAgentKey(storage),
    );
    const agent = resolveMemberAgent({
        agentKey,
        allowExperimentalAgents: showDevTools,
    });

    selectionRef.current = { modelId, reasoningEffort };

    const applySelection = useCallback(
        (selection: SproutStreamSelection, persist: boolean) => {
            const clamped = clampSproutStreamSelection(selection);
            selectionRef.current = clamped;
            setModelIdState(clamped.modelId);
            setReasoningEffortState(clamped.reasoningEffort);
            if (persist) {
                writeSproutStreamSelection(
                    storage,
                    clamped,
                    threadIdRef.current,
                );
            }
        },
        [storage],
    );

    useEffect(() => {
        if (!showDevTools || threadId == null || threadId === '') {
            return undefined;
        }

        const fromStore = readSproutStreamSelectionForThread(storage, threadId);
        if (fromStore != null) {
            applySelection(fromStore, false);
            return undefined;
        }

        let cancelled = false;

        const hydrateFromThread = async () => {
            const applyIfUnchanged = (selection: SproutStreamSelection) => {
                if (
                    cancelled ||
                    readSproutStreamSelectionForThread(storage, threadId) !=
                        null
                ) {
                    return;
                }

                applySelection(selection, true);
            };

            try {
                const thread = await mastraClientRef.current
                    .getMemoryThread({
                        threadId,
                        agentId: SPROUT_AGENT.agentKey,
                    })
                    .get();
                if (cancelled) {
                    return;
                }

                const metricsSelection = getSproutStreamSelection(
                    thread.metadata,
                );
                const parsed = parseSproutStreamOverrides({
                    model: metricsSelection.model,
                    reasoningEffort: metricsSelection.reasoningEffort,
                });
                applyIfUnchanged(
                    clampSproutStreamSelection({
                        modelId: parsed.model,
                        reasoningEffort: parsed.reasoningEffort,
                    }),
                );
            } catch (err) {
                if (cancelled) {
                    return;
                }

                if (!isThreadNotFoundError(err)) {
                    captureException(err);
                }

                applyIfUnchanged(
                    readSproutStreamOptionsStore(storage).lastUsed,
                );
            }
        };

        void hydrateFromThread();

        return () => {
            cancelled = true;
        };
    }, [applySelection, showDevTools, storage, threadId]);

    useEffect(() => {
        if (!showDevTools || !isWindowDefined) {
            return undefined;
        }

        const handleStorageEvent = (event: StorageEvent) => {
            if (event.key !== StorageKeys.SPROUT_STREAM_OPTIONS) {
                return;
            }

            const activeThreadId = threadIdRef.current;
            if (activeThreadId == null || activeThreadId === '') {
                return;
            }

            const fromStore = readSproutStreamSelectionForThread(
                storage,
                activeThreadId,
            );
            if (fromStore == null) {
                return;
            }

            applySelection(fromStore, false);
        };

        window.addEventListener('storage', handleStorageEvent, true);
        return () => {
            window.removeEventListener('storage', handleStorageEvent, true);
        };
    }, [applySelection, showDevTools, storage]);

    const setModelId = useCallback(
        (next: SproutStreamModelId) => {
            applySelection(
                {
                    modelId: next,
                    reasoningEffort: selectionRef.current.reasoningEffort,
                },
                true,
            );
        },
        [applySelection],
    );

    const setReasoningEffort = useCallback(
        (next: SproutStreamReasoningEffort) => {
            applySelection(
                {
                    modelId: selectionRef.current.modelId,
                    reasoningEffort: next,
                },
                true,
            );
        },
        [applySelection],
    );

    const setAgentKey = useCallback(
        (next: string) => {
            if (!showDevTools) {
                return;
            }

            const resolved = resolveMemberAgent({
                agentKey: next,
                allowExperimentalAgents: true,
            });
            setAgentKeyState(resolved.agentKey);
            writeStoredMemberAgentKey(storage, resolved.agentKey);
        },
        [showDevTools, storage],
    );

    return {
        modelId,
        reasoningEffort,
        setModelId,
        setReasoningEffort,
        agent,
        setAgentKey,
    };
};
